'use client'

import { Form, Input } from 'antd'
import type { PasswordTexts } from './texts'

/** Props of {@link PasswordWithConfirm}. */
export interface PasswordWithConfirmProps {
  texts: PasswordTexts
  /**
   * Shortest password the form accepts. Without it only the API checks the length, and refuses
   * with `PASSWORD_NOT_GOOD_ENOUGH`.
   */
  minLength?: number
}

/** A rule that passes when `pattern` matches the value. */
const requires = (pattern: RegExp, message: string) => ({
  validator: (_: unknown, value: unknown) =>
    typeof value === 'string' && pattern.test(value)
      ? Promise.resolve()
      : Promise.reject(new Error(message)),
})

/**
 * The `password` and `confirm` fields of a form that sets a new password. The password needs a
 * digit, an upper and a lower case letter and a special character; `confirm` must repeat it.
 */
export function PasswordWithConfirm({ texts, minLength }: PasswordWithConfirmProps) {
  return (
    <>
      <Form.Item
        name="password"
        label={texts.passwordLabel}
        rules={[
          { message: texts.passwordRequired, required: true },
          ...(minLength === undefined
            ? []
            : [{ message: texts.minLength(minLength), min: minLength }]),
          requires(/[0-9]/, texts.digitRequired),
          requires(/[A-Z]/, texts.uppercaseRequired),
          requires(/[a-z]/, texts.lowercaseRequired),
          requires(/[^A-Za-z0-9]/, texts.specialCharacterRequired),
        ]}
        hasFeedback
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>

      <Form.Item
        name="confirm"
        label={texts.confirmLabel}
        dependencies={['password']}
        hasFeedback
        rules={[
          { message: texts.confirmRequired, required: true },
          ({ getFieldValue }) => ({
            validator: (_: unknown, value: unknown) =>
              !value || getFieldValue('password') === value
                ? Promise.resolve()
                : Promise.reject(new Error(texts.confirmMismatch)),
          }),
        ]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
    </>
  )
}
